import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level safety net. If any React subtree throws, we render a friendly
 * recovery card instead of a white screen — and offer the user a quick way
 * back to home or a hard refresh.
 *
 * This catches *render* errors. Async errors inside event handlers / fetches
 * are still caught by individual try/catch blocks.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // No analytics yet — just log for browser devtools.
    console.error('[Nako Pula crash]', error, info.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="bg-paper flex min-h-screen items-center justify-center p-6">
        <div className="border-line shadow-card max-w-mobile rounded-card-lg w-full border bg-white p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FEE2E2] text-[#991B1B]">
            <AlertTriangle size={24} />
          </div>
          <h1 className="font-display mb-2 text-2xl font-semibold">Something went wrong</h1>
          <p className="text-muted mb-4 text-sm">
            We've logged the issue and we're sorry for the disruption. Try refreshing — your data is
            safe.
          </p>
          {this.state.error?.message && (
            <pre className="text-muted bg-paper-2 mb-4 max-h-24 overflow-auto rounded p-2 text-left font-mono text-[11px]">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => window.location.reload()}
              className="bg-accent rounded-btn min-h-tap inline-flex items-center justify-center gap-2 px-4 font-medium text-white"
            >
              <RotateCw size={16} /> Refresh
            </button>
            <a
              href="/"
              className="text-muted hover:text-ink rounded-btn min-h-tap inline-flex items-center justify-center text-sm"
            >
              Back to home
            </a>
          </div>
        </div>
      </main>
    );
  }
}
